import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * PaymentTest — Test page that mimics the NHIntake payment flow.
 * Tests both Stripe Elements and Elavon EPG Lightbox based on practice settings.
 * Route: /practice-view/payment-test
 */
export default function PaymentTest({ practiceId }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gateway, setGateway] = useState('STRIPE');
  const [amount, setAmount] = useState('40.00');
  const [status, setStatus] = useState('');
  const [paymentResult, setPaymentResult] = useState(null);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeElements, setStripeElements] = useState(null);
  const [stripeInstance, setStripeInstance] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Load practice settings
  const loadSettings = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const result = await api.get(`/practices/${practiceId}/payments/settings`);
      if (result.success) {
        setSettings(result.data);
        setGateway(result.data.payment_gateway || 'STRIPE');
      }
    } catch (err) {
      setStatus('Error loading settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Load Stripe.js when gateway is STRIPE
  useEffect(() => {
    if (gateway !== 'STRIPE') return;
    if (window.Stripe) { setStripeLoaded(true); return; }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => setStripeLoaded(true);
    document.head.appendChild(script);
  }, [gateway]);

  // ─── Stripe Payment Flow ───────────────────────────────

  const handleStripePayment = async () => {
    setProcessing(true);
    setStatus('Creating Stripe PaymentIntent...');
    setPaymentResult(null);

    try {
      // Step 1: Get publishable key
      const settingsRes = await api.get(`/practices/${practiceId}/payments/settings`);
      const publishableKey = settingsRes.data?.gateway_public_key;
      if (!publishableKey) {
        setStatus('Error: No Stripe publishable key configured in practice settings');
        setProcessing(false);
        return;
      }

      // Step 2: Initialize Stripe
      const stripe = window.Stripe(publishableKey);
      setStripeInstance(stripe);

      // Step 3: Create PaymentIntent via backend
      // Note: In real intake flow, this uses a payment link token.
      // For testing, we simulate directly.
      setStatus('Stripe ready — enter card details below and click Pay');

      // Create Stripe Elements
      const elements = stripe.elements();
      const cardElement = elements.create('card', {
        style: {
          base: { fontSize: '16px', color: '#374151', fontFamily: 'system-ui, sans-serif', '::placeholder': { color: '#9ca3af' } },
          invalid: { color: '#dc2626' },
        },
      });

      // Mount to DOM
      const mountEl = document.getElementById('stripe-card-element');
      if (mountEl) {
        mountEl.innerHTML = '';
        cardElement.mount(mountEl);
        setStripeElements(cardElement);
        setStatus('Enter card details (test: 4242 4242 4242 4242, any future exp, any CVC)');
      }
    } catch (err) {
      setStatus('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleStripeConfirm = async () => {
    if (!stripeInstance || !stripeElements) return;
    setProcessing(true);
    setStatus('Processing Stripe payment...');

    try {
      // Create a PaymentMethod from the card element
      const { error, paymentMethod } = await stripeInstance.createPaymentMethod({
        type: 'card',
        card: stripeElements,
      });

      if (error) {
        setStatus('Card error: ' + error.message);
        setProcessing(false);
        return;
      }

      setPaymentResult({
        gateway: 'STRIPE',
        paymentMethodId: paymentMethod.id,
        brand: paymentMethod.card?.brand,
        last4: paymentMethod.card?.last4,
        expMonth: paymentMethod.card?.exp_month,
        expYear: paymentMethod.card?.exp_year,
      });
      setStatus('Stripe PaymentMethod created successfully! Card tokenized — ready for charge.');
    } catch (err) {
      setStatus('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  // ─── Elavon EPG Payment Flow ───────────────────────────

  const handleElavonPayment = async () => {
    setProcessing(true);
    setStatus('Creating Elavon EPG Payment Session...');
    setPaymentResult(null);

    try {
      // Call our backend to create Order + Payment Session
      const result = await api.post('/payments/elavon-test', {
        practiceId,
        amount: parseFloat(amount),
      });

      if (!result.success) {
        setStatus('Error: ' + (result.error || 'Failed to create payment session'));
        setProcessing(false);
        return;
      }

      const { sessionId, sessionUrl, merchantAlias, publicKey, isSandbox } = result.data;

      setPaymentResult({
        gateway: 'ELAVON_EPG',
        sessionId,
        sessionUrl,
        merchantAlias,
        publicKey,
        isSandbox,
        status: 'Session created — open URL to complete payment',
      });

      setStatus(`EPG Payment Session created! Session ID: ${sessionId}`);

      // If sessionUrl is available, we can open it in an iframe or new window
      if (sessionUrl) {
        setStatus(`Session ready. Open the payment URL to test the Lightbox.`);
      }
    } catch (err) {
      setStatus('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  // ─── Render ────────────────────────────────────────────

  if (loading || !settings) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading payment settings...</div>;
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: 800 }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.5rem' }}>Payment Integration Test</h1>
      <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
        Mimics the NHIntake payment flow. Tests Stripe Elements and Elavon EPG Lightbox.
      </p>

      {/* Status Banner */}
      {status && (
        <div style={{
          padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.875rem',
          background: status.includes('Error') ? '#fef2f2' : status.includes('success') ? '#f0fdf4' : '#eff6ff',
          color: status.includes('Error') ? '#dc2626' : status.includes('success') ? '#16a34a' : '#2563eb',
          border: `1px solid ${status.includes('Error') ? '#fecaca' : status.includes('success') ? '#bbf7d0' : '#bfdbfe'}`,
        }}>
          {status}
        </div>
      )}

      {/* Gateway Selection */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1.25rem', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>Gateway</h3>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="radio" value="STRIPE" checked={gateway === 'STRIPE'} onChange={() => setGateway('STRIPE')} />
            <span style={{ fontSize: '0.875rem' }}>Stripe Elements</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="radio" value="ELAVON" checked={gateway === 'ELAVON'} onChange={() => setGateway('ELAVON')} />
            <span style={{ fontSize: '0.875rem' }}>Elavon EPG</span>
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label style={{ fontSize: '0.875rem', color: '#374151' }}>Amount: $</label>
          <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 100, fontSize: '0.875rem' }} />
        </div>
      </div>

      {/* Stripe Section */}
      {gateway === 'STRIPE' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1.25rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>
            Stripe Elements
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: stripeLoaded ? '#16a34a' : '#dc2626', marginLeft: 8 }}>
              {stripeLoaded ? '(Stripe.js loaded)' : '(loading Stripe.js...)'}
            </span>
          </h3>

          <div style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 8 }}>
              Publishable Key: <code style={{ fontSize: '0.75rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                {settings.gateway_public_key ? settings.gateway_public_key.substring(0, 20) + '...' : '(not set)'}
              </code>
            </p>
          </div>

          {!stripeElements && (
            <button onClick={handleStripePayment} disabled={processing || !stripeLoaded}
              style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#635bff', color: '#fff', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>
              {processing ? 'Initializing...' : 'Initialize Stripe Card Input'}
            </button>
          )}
          <div id="stripe-card-element" style={{
            padding: '12px', border: '1px solid #d1d5db', borderRadius: 6, marginBottom: '1rem', background: '#fafafa',
            display: stripeElements ? 'block' : 'none',
          }} />
          {stripeElements && (
            <button onClick={handleStripeConfirm} disabled={processing}
              style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#635bff', color: '#fff', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>
              {processing ? 'Processing...' : `Tokenize Card ($${amount})`}
            </button>
          )}
        </div>
      )}

      {/* Elavon EPG Section */}
      {gateway === 'ELAVON' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1.25rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>
            Elavon Payment Gateway (EPG)
          </h3>

          <div style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 4 }}>
              Merchant Alias: <code style={{ fontSize: '0.75rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                {settings.elavon_user_id || '(not set)'}
              </code>
            </p>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 4 }}>
              Public Key: <code style={{ fontSize: '0.75rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                {settings.elavon_terminal_id ? settings.elavon_terminal_id.substring(0, 20) + '...' : '(not set)'}
              </code>
            </p>
            <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>
              Sandbox: <code style={{ fontSize: '0.75rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                {process.env.REACT_APP_ELAVON_SANDBOX === 'true' ? 'Yes' : 'Production'}
              </code>
            </p>
          </div>

          <button onClick={handleElavonPayment} disabled={processing}
            style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#1a73e8', color: '#fff', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>
            {processing ? 'Creating Session...' : `Create Payment Session ($${amount})`}
          </button>

          {paymentResult?.sessionId && (
            <div style={{ marginTop: '1rem' }}>
              <p style={{ fontSize: '0.8rem', color: '#374151', marginBottom: 8 }}>
                Session ID: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{paymentResult.sessionId}</code>
              </p>
              <button onClick={() => {
                // Load ElavonLightbox SDK if not already loaded
                const scriptUrl = paymentResult.isSandbox
                  ? 'https://uat.hpp.converge.eu.elavonaws.com/client/library.js'
                  : 'https://hpp.na.elavonpayments.com/client/library.js';

                const openLightbox = () => {
                  if (!window.ElavonLightbox) {
                    setStatus('Error: ElavonLightbox SDK not loaded');
                    return;
                  }
                  const lightbox = new window.ElavonLightbox({
                    sessionId: paymentResult.sessionId,
                    onReady: (error) => {
                      if (error) {
                        setStatus('Error: Lightbox failed to load — ' + error.message);
                      } else {
                        setStatus('Elavon Lightbox opened — enter card details');
                        lightbox.show();
                      }
                    },
                    messageHandler: (message, defaultAction) => {
                      switch (message.type) {
                        case 'transactionCreated':
                          setPaymentResult(prev => ({
                            ...prev,
                            transactionId: message.transaction,
                            isAuthorized: message.isAuthorized,
                            messageType: 'transactionCreated',
                          }));
                          setStatus('Elavon payment ' + (message.isAuthorized ? 'approved!' : 'declined'));
                          break;
                        case 'hostedCardCreated':
                          setPaymentResult(prev => ({
                            ...prev,
                            hostedCardId: message.hostedCard,
                            messageType: 'hostedCardCreated',
                          }));
                          setStatus('Card tokenized — hostedCardId: ' + message.hostedCard);
                          break;
                        case 'error':
                          setStatus('Elavon error: ' + (message.error || 'unknown'));
                          break;
                        default:
                          break;
                      }
                      defaultAction();
                    }
                  });
                };

                if (window.ElavonLightbox) {
                  openLightbox();
                } else {
                  const script = document.createElement('script');
                  script.src = scriptUrl;
                  script.onload = openLightbox;
                  script.onerror = () => setStatus('Error: Failed to load ElavonLightbox SDK from ' + scriptUrl);
                  document.head.appendChild(script);
                }
              }}
                style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#059669', color: '#fff', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>
                Open Elavon Lightbox
              </button>
            </div>
          )}
        </div>
      )}

      {/* Result Display */}
      {paymentResult && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '1.25rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem', color: '#166534' }}>Result</h3>
          <pre style={{ fontSize: '0.75rem', color: '#374151', background: '#fff', padding: '0.75rem', borderRadius: 6, overflow: 'auto', maxHeight: 300 }}>
            {JSON.stringify(paymentResult, null, 2)}
          </pre>
        </div>
      )}

      {/* Info Panel */}
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '1rem', fontSize: '0.8rem', color: '#92400e' }}>
        <strong>Test Cards:</strong><br />
        Stripe: <code>4242 4242 4242 4242</code> (any future exp, any CVC)<br />
        Stripe decline: <code>4000 0000 0000 0002</code><br />
        Elavon: <code>4000 0000 0000 0002</code> (amount must end in .00)<br />
        Elavon decline: amount = $1.05
      </div>
    </div>
  );
}
